import { ENEMY_TRAY_ORDER } from '../game/model/enemies'
import { ENEMY_DEFINITIONS_V2 } from '../game/combat/enemy/catalog'
import { resolveEnemyStats } from '../game/combat/enemy/resolveEnemyStats'
import type { MapDefinition, WaveDefinition, WaveGroupDefinition } from '../game/model/types'

export const EDITOR_ENEMY_OPTIONS = ENEMY_TRAY_ORDER.map((enemy) => ({
  id: enemy.id,
  label: enemy.name.toUpperCase()
}))

export type EnemyFamilyId = (typeof EDITOR_ENEMY_OPTIONS)[number]['id']

/** Counts keyed by enemy definition id, plus total. */
export type WaveComposition = Record<string, number> & { total: number }

export interface WaveSummary {
  id: string
  index: number
  reward: number
  composition: WaveComposition
  label: string
}

export interface MapWaveSummary {
  waves: WaveSummary[]
  totalEnemies: number
  totalRewards: number
  composition: WaveComposition
}

function emptyComposition(): WaveComposition {
  const composition: WaveComposition = { total: 0 }
  for (const enemy of ENEMY_TRAY_ORDER) {
    composition[enemy.id] = 0
  }
  return composition
}

export function groupEnemyCount(groups: WaveGroupDefinition[]): WaveComposition {
  const composition = emptyComposition()
  for (const group of groups) {
    const count = Number.isFinite(group.count) ? group.count : 0
    composition.total += count
    if (composition[group.enemy] === undefined) {
      composition[group.enemy] = 0
    }
    composition[group.enemy] += count
  }
  return composition
}

export function formatComposition(composition: WaveComposition): string {
  const parts: string[] = []
  for (const enemy of ENEMY_TRAY_ORDER) {
    const count = composition[enemy.id] ?? 0
    if (count > 0) parts.push(`${count} ${enemy.name.toUpperCase()}`)
  }
  for (const [id, count] of Object.entries(composition)) {
    if (id === 'total' || ENEMY_TRAY_ORDER.some((e) => e.id === id)) continue
    if (count > 0) parts.push(`${count} ${id.toUpperCase()}`)
  }
  return parts.join(' · ') || '0 enemies'
}

export function summarizeWave(wave: WaveDefinition, index: number): WaveSummary {
  const composition = groupEnemyCount(wave.groups ?? [])
  return {
    id: wave.id,
    index,
    reward: wave.completionReward,
    composition,
    label: `W${index + 1}`
  }
}

export function summarizeMapWaves(map: Pick<MapDefinition, 'waves'>): MapWaveSummary {
  const waves = (map.waves ?? []).map((wave, index) => summarizeWave(wave, index))
  const composition = emptyComposition()
  let totalRewards = 0
  for (const wave of waves) {
    for (const [id, count] of Object.entries(wave.composition)) {
      if (id === 'total') continue
      if (composition[id] === undefined) composition[id] = 0
      composition[id] += count
    }
    composition.total += wave.composition.total
    totalRewards += wave.reward
  }
  return {
    waves,
    totalEnemies: composition.total,
    totalRewards,
    composition
  }
}

export function nextWaveId(waves: WaveDefinition[]): string {
  const used = new Set(waves.map((w) => w.id))
  let n = 1
  while (used.has(`wave-${n}`)) n += 1
  return `wave-${n}`
}

export function createEmptyGroup(): WaveGroupDefinition {
  return { enemy: 'normal', count: 1, interval: 1, delay: 0 }
}

export function createEmptyWave(waves: WaveDefinition[]): WaveDefinition {
  return {
    id: nextWaveId(waves),
    groups: [createEmptyGroup()],
    completionReward: 0
  }
}

/** Theoretical kill gold if every enemy in the map waves is killed once. */
export function theoreticalKillRewards(
  map: Pick<MapDefinition, 'waves'>,
  catalog: Record<string, { reward: number }> = Object.fromEntries(
    ENEMY_TRAY_ORDER.map((e) => [e.id, e])
  )
): number {
  let total = 0
  for (const wave of map.waves ?? []) {
    for (const group of wave.groups ?? []) {
      const v2 = ENEMY_DEFINITIONS_V2[group.enemy]
      let reward = catalog[group.enemy]?.reward ?? 0
      if (v2) {
        reward = resolveEnemyStats(v2, {
          enemyLevel: group.enemyLevel ?? 1,
          enemyRank:
            group.enemyRank ??
            v2.defaultEnemyRank ??
            (group.enemy === 'boss' || group.enemy === 'air_boss' ? 'BOSS' : 'NORMAL')
        }).reward
      }
      total += reward * (Number.isFinite(group.count) ? group.count : 0)
    }
  }
  return total
}

export function theoreticalWaveRewards(map: Pick<MapDefinition, 'waves'>): number {
  return (map.waves ?? []).reduce((sum, wave) => sum + (wave.completionReward ?? 0), 0)
}

export function theoreticalCampaignIncome(
  map: Pick<MapDefinition, 'waves'>,
  catalog?: Record<string, { reward: number }>
): { killRewards: number; waveRewards: number; combined: number } {
  const killRewards = theoreticalKillRewards(map, catalog)
  const waveRewards = theoreticalWaveRewards(map)
  return { killRewards, waveRewards, combined: killRewards + waveRewards }
}

export function duplicateWave(wave: WaveDefinition, waves: WaveDefinition[]): WaveDefinition {
  return {
    id: nextWaveId(waves),
    completionReward: wave.completionReward,
    groups: wave.groups.map((group) => ({ ...group }))
  }
}

export function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (from < 0 || from >= items.length || to < 0 || to >= items.length || from === to) {
    return items
  }
  const next = [...items]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  return next
}
